/**
 * The forms list (RESTORE_PLAN R1.4): status labels for the live lifecycle, the change history in
 * words with relative times, and grouping under a team. From the pre-November RequestsList.
 */

export interface StatusHistoryEntry {
  action: string;
  from: string;
  to: string;
  at: string;
  by: { id: string; name: string; role?: string };
  reason?: string;
}

/** The live lifecycle (serviceProjectRoutes RequestTypeStatus) and the older list's names, as one map. */
export const STATUS_LABELS: Record<string, { label: string; tone: 'neutral' | 'info' | 'progress' | 'good' | 'bad' | 'warn' }> = {
  draft: { label: 'Draft', tone: 'neutral' },
  awaiting_client: { label: 'Awaiting client', tone: 'warn' },
  ready_for_build: { label: 'Ready for build', tone: 'info' },
  in_build: { label: 'In build', tone: 'progress' },
  ready_for_test: { label: 'Ready for test', tone: 'info' },
  in_test: { label: 'In testing', tone: 'progress' },
  approved: { label: 'Approved', tone: 'good' },
  blocked: { label: 'Blocked', tone: 'bad' },
  cancelled: { label: 'Canceled', tone: 'neutral' },
  draft_revision: { label: 'Revision in progress', tone: 'warn' },
  // older names some lists used
  'ready-to-build': { label: 'Ready for build', tone: 'info' },
  building: { label: 'In build', tone: 'progress' },
  'in-review': { label: 'In testing', tone: 'progress' },
  done: { label: 'Approved', tone: 'good' },
};

export function statusLabel(status: string | undefined): { label: string; tone: string } {
  return STATUS_LABELS[status || 'draft'] || { label: status || 'Draft', tone: 'neutral' };
}

/** The statuses that count as finished, and as in progress, in the list's summary tiles. */
export const FINISHED_STATUSES = ['approved', 'done'];
export const IN_PROGRESS_STATUSES = ['draft', 'draft_revision', 'awaiting_client', 'ready_for_build', 'in_build', 'ready_for_test', 'in_test', 'building', 'ready-to-build', 'in-review'];

const ACTION_WORDS: Record<string, string> = {
  created: 'Created',
  mark_ready_for_build: 'Marked ready for build',
  start_build: 'Started the build',
  mark_ready_for_test: 'Marked ready for test',
  approve: 'Approved',
  reject: 'Sent back to build',
  unlock: 'Reopened for changes',
};

export function historyActionLabel(entry: Pick<StatusHistoryEntry, 'action' | 'to'>): string {
  return ACTION_WORDS[entry.action] || `Moved to ${statusLabel(entry.to).label.toLowerCase()}`;
}

/** "Just now", "5 minutes ago", "3 days ago", or a short date past a week. */
export function formatRelativeTime(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const diffMs = now.getTime() - date.getTime();
  const mins = Math.floor(diffMs / 60000);
  const hours = Math.floor(diffMs / 3600000);
  const days = Math.floor(diffMs / 86400000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
}

/** Newest first. */
export function sortHistory(entries: StatusHistoryEntry[] | undefined): StatusHistoryEntry[] {
  return [...(entries || [])].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
}

export const NO_TEAM = 'No team';

/** Request types under their owning team, teams alphabetically, the ones without a team last. */
export function groupByTeam<T extends { team?: string }>(items: T[]): { team: string; items: T[] }[] {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const key = item.team?.trim() || NO_TEAM;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(item);
  }
  const named = [...groups.keys()].filter((k) => k !== NO_TEAM).sort((a, b) => a.localeCompare(b));
  const order = groups.has(NO_TEAM) ? [...named, NO_TEAM] : named;
  return order.map((team) => ({ team, items: groups.get(team)! }));
}
